import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Heading from 'shopper/components/Heading';

import ErrorBoundary from 'components/ErrorBoundary';
import StopWatch from 'components/StopWatch';

import useCountdown from 'hooks/useCountdown';
import useFlashOffer from 'hooks/useFlashOffer';

import { sendEvent } from 'lib/gtag';
import { sliceWithEllipsis } from 'lib/string';

import CommentsList from '../CommentsList';
import PriceComparatorAlert from '../PriceComparatorAlert';
import ActionButton from './ActionButton';
import Authors from './Authors';
import Hero from './Hero';
import Price from './Price';
import PublishDetails from './PublishDetails';
import SocialActionsStats from './SocialActionsStats';

const Tags = dynamic(() => import('./Tags'));

const Main = ({
  aliasId,
  authorCount,
  authors,
  gaEventCategory,
  isFinished,
  isOfferCommentsAllowed,
  isPromobitHasTheBestPrice = false,
  isShowingPriceComparator,
  keywords,
  mostRelevantKeyword,
  offerCommentsCount,
  offerCoupon,
  offerCta = null,
  offerId,
  offerInstructions,
  offersToCompare,
  offerLikes,
  offerOldPrice,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerSlug,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerUserVisibility,
  serverComments,
  storeDomain,
  storeImage,
  storeName,
  totalReviews,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const hasComparator = (offersToCompare ?? []).length > 0;
  const flashOffer = useFlashOffer(offerId);
  const countdown = useCountdown(flashOffer?.targetDate || new Date(0));
  const showStopWatch = Boolean(flashOffer);

  const onOfferTitleClick = () => {
    sendEvent({ action: 'title', category: gaEventCategory });
  };

  return (
    <section className="flex flex-col gap-4 lg:max-h-[550px] lg:flex-row">
      <div className="flex flex-col gap-2 lg:w-[624px] lg:gap-4 xl:w-[714px]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <Hero
            gaEventCategory={gaEventCategory}
            isFinished={isFinished}
            offerId={offerId}
            offerPhoto={offerPhoto}
            offerTitle={offerTitle}
          />
          {showStopWatch && (
            <div className="w-full lg:hidden">
              <ErrorBoundary>
                <StopWatch
                  hours={countdown.hours}
                  minutes={countdown.minutes}
                  phase={flashOffer.phase}
                  seconds={countdown.seconds}
                  size="lg"
                />
              </ErrorBoundary>
            </div>
          )}
          <Heading
            as="h1"
            className="text-pretty text-base lg:hidden"
            size="size4"
            onClick={onOfferTitleClick}
          >
            {sliceWithEllipsis(offerTitle, 180)}
          </Heading>
          <div className="flex flex-col gap-x-6 gap-y-2 lg:hidden">
            {isShowingPriceComparator && (
              <PriceComparatorAlert
                gaEventCategory={gaEventCategory}
                isPromobitHasTheBestPrice={isPromobitHasTheBestPrice}
                offerOldPrice={offerOldPrice}
                offerPrice={offerPrice}
                offerPriceType={offerPriceType}
              />
            )}
            {(!isShowingPriceComparator || !isPromobitHasTheBestPrice) && (
              <Price
                offerOldPrice={offerOldPrice}
                offerPrice={offerPrice}
                offerPriceType={offerPriceType}
                size="large"
              />
            )}
            <ActionButton
              gaEventCategory={gaEventCategory}
              hasComparator={hasComparator}
              isFinished={isFinished}
              mostRelevantKeyword={mostRelevantKeyword}
              offerCoupon={offerCoupon}
              offerCta={offerCta}
              offerId={offerId}
              offerSlug={offerSlug}
              offerUserVisibility={offerUserVisibility}
            />
          </div>
          <div className="flex flex-col gap-4 xl:min-w-[360px]">
            <div className="flex flex-col gap-2 lg:gap-4">
              <SocialActionsStats
                aliasId={aliasId}
                className="-order-3 lg:order-none"
                gaEventCategory={gaEventCategory}
                keywords={keywords}
                offerCommentsCount={offerCommentsCount}
                offerCoupon={offerCoupon}
                offerId={offerId}
                offerInstructions={offerInstructions}
                offerLikes={offerLikes}
                offerPrice={offerPrice}
                offerPriceType={offerPriceType}
                offerStatusName={offerStatusName}
                offerTitle={offerTitle}
                totalReviews={totalReviews}
                userId={userId}
              />
              <Heading
                as="h1"
                className="hidden text-pretty lg:order-none lg:flex lg:text-xl"
                size="size4"
                onClick={onOfferTitleClick}
              >
                {sliceWithEllipsis(offerTitle, 180)}
              </Heading>
              {offerTags.length > 0 && (
                <Tags className="-order-2 lg:order-none" tags={offerTags} />
              )}
              <PublishDetails
                className="-order-3 lg:order-none"
                storeDomain={storeDomain}
                storeImage={storeImage}
                storeName={storeName}
              />
              {showStopWatch && (
                <div className="hidden lg:block">
                  <ErrorBoundary>
                    <StopWatch
                      hours={countdown.hours}
                      minutes={countdown.minutes}
                      offerId={offerId}
                      phase={flashOffer.phase}
                      seconds={countdown.seconds}
                      size="lg"
                    />
                  </ErrorBoundary>
                </div>
              )}
              <div className="hidden flex-col gap-x-6 gap-y-2 lg:flex">
                <Price
                  offerOldPrice={offerOldPrice}
                  offerPrice={offerPrice}
                  offerPriceType={offerPriceType}
                  size="large"
                />
                <ActionButton
                  gaEventCategory={gaEventCategory}
                  hasComparator={hasComparator}
                  isFinished={isFinished}
                  mostRelevantKeyword={mostRelevantKeyword}
                  offerCoupon={offerCoupon}
                  offerCta={offerCta}
                  offerId={offerId}
                  offerSlug={offerSlug}
                  offerUserVisibility={offerUserVisibility}
                />
                <Authors
                  authorCount={authorCount}
                  authors={authors}
                  offerId={offerId}
                  offerPublished={offerPublished}
                  userId={userId}
                  userName={userName}
                  userSpecialtyName={userSpecialtyName}
                  userUsername={userUsername}
                />
              </div>
            </div>
          </div>
          <Authors
            authorCount={authorCount}
            authors={authors}
            className="flex lg:hidden"
            offerId={offerId}
            offerPublished={offerPublished}
            userId={userId}
            userName={userName}
            userSpecialtyName={userSpecialtyName}
            userUsername={userUsername}
          />
        </div>
        <div className="hidden w-full lg:flex">
          {isShowingPriceComparator && (
            <PriceComparatorAlert
              gaEventCategory={gaEventCategory}
              isPromobitHasTheBestPrice={isPromobitHasTheBestPrice}
              offerOldPrice={offerOldPrice}
              offerPrice={offerPrice}
              offerPriceType={offerPriceType}
            />
          )}
        </div>
      </div>
      <CommentsList
        aliasId={aliasId}
        className="hidden lg:flex lg:w-[400px] xl:w-[550px]"
        gaEventCategory={gaEventCategory}
        isOfferCommentsAllowed={isOfferCommentsAllowed}
        isShowingPriceComparator={isShowingPriceComparator}
        offerCommentsCount={offerCommentsCount}
        offerId={offerId}
        offerStatusName={offerStatusName}
        serverComments={serverComments}
      />
    </section>
  );
};

Main.propTypes = {
  aliasId: PropTypes.number,
  authorCount: PropTypes.number.isRequired,
  authors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  isFinished: PropTypes.bool.isRequired,
  isOfferCommentsAllowed: PropTypes.bool.isRequired,
  isPromobitHasTheBestPrice: PropTypes.bool,
  keywords: PropTypes.arrayOf(PropTypes.string),
  mostRelevantKeyword: PropTypes.string.isRequired,
  offerCommentsCount: PropTypes.number.isRequired,
  offerCoupon: PropTypes.string,
  offerCta: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerLikes: PropTypes.number.isRequired,
  offersToCompare: PropTypes.arrayOf(PropTypes.shape()),
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(PropTypes.shape()),
  offerTitle: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        id: PropTypes.number.isRequired,
        relevance: PropTypes.number.isRequired,
        text: PropTypes.string.isRequired,
        raw: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        likes: PropTypes.number.isRequired,
        statusName: PropTypes.string.isRequired,
        stars: PropTypes.number.isRequired,
        user: PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          username: PropTypes.string.isRequired,
          photo: PropTypes.string.isRequired,
          typeName: PropTypes.string.isRequired,
          level: PropTypes.number.isRequired,
          specialityName: PropTypes.string,
          link: PropTypes.string.isRequired,
        }).isRequired,
      }),
    })
  ),
  serverComments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.number.isRequired,
      parentId: PropTypes.number,
      userId: PropTypes.number.isRequired,
      commentText: PropTypes.string.isRequired,
      commentRaw: PropTypes.string.isRequired,
      commentDate: PropTypes.string.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool.isRequired,
      relevance: PropTypes.number.isRequired,
      commentStars: PropTypes.number,
      reviewAliasId: PropTypes.number,
      edited: PropTypes.bool.isRequired,
      isReview: PropTypes.bool.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    })
  ).isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  totalReviews: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
};

export default memo(Main);
